import React from 'react'
import PropTypes from 'prop-types'
import detail from './detail.module.css'

const TABLE = { artist: 'mmuf_artist', release: 'mmuf_song_album', song: 'mmuf_song' }

const TYPE_LABEL = { artist: 'Artist', release: 'Release', song: 'Song' }

// The complete set mergeManagedFields can ever preserve (catalogue.js).
const FIELD_LABEL = {
  name: 'Name',
  name_full: 'Full title',
  album_artist_name: 'Release artist credit',
  song_artist_name: 'Song artist credit',
  album_type: 'Release type',
  year_release: 'Release year',
}

// Keyed off `code`, never the server's prose.
const ART_REASONS = {
  NOT_LINKED: 'Uploaded, but had not appeared on the release when we stopped checking. It may be there now.',
  IMAGE_SET_BY_EDITOR: 'Skipped, because someone had already set this cover by hand.',
  ART_TOO_SMALL: 'YouTube’s image was too small to use.',
  ART_TOO_LARGE: 'YouTube’s image was too large to use.',
  ART_TYPE_UNRECOGNIZED: 'YouTube’s image was in a format we cannot read.',
  ART_DIMENSIONS_UNREADABLE: 'YouTube’s image was unreadable.',
  ARTIST_IMAGE_NOT_SQUARE: 'The photo on the YouTube channel is not square, so it was not used.',
  ARTIST_AVATAR_NOT_FOUND: 'No usable photo was found on the YouTube channel.',
}

const AVATAR_CAUSES = {
  no_channel: 'This artist has no YouTube channel page.',
  not_found: 'The channel page had no photo we could use.',
  fetch_failed: 'We could not reach the channel page this time.',
  not_square: 'The only photo on the channel is not square.',
  host_not_allowed: 'The photo is hosted somewhere we do not accept images from.',
}

const REQUEST_ART = {
  COVER_ART_FAILED:
    'Cover art stopped early after too many images failed in a row. The releases and songs are all saved.',
  COVER_ART_DID_NOT_FINISH:
    'Cover art never ran. The releases and songs are all saved, but no artwork was added.',
  CANCELLED: 'This was stopped while cover art was being added. Everything already saved was kept.',
}

const VIDEO_REASONS = {
  VIDEO_UNAVAILABLE: 'YouTube will not play this video, so there was no file to add.',
  VIDEO_CHECK_FAILED: 'The file that came down did not match the expected recording, so it was not used.',
  VIDEO_DOWNLOAD_FAILED: 'The download did not finish this time. The next sync of this artist will try again.',
  VIDEO_UPLOAD_FAILED: 'The video downloaded but could not be saved. The next sync will try again.',
}

const VIDEO_CAUSES = {
  VIDEO_BOT_CHECK: 'YouTube asked us to prove we are not a bot.',
  VIDEO_TOOLS_MISSING: 'The worker is missing the video downloader — this one needs an engineer.',
}

const REQUEST_VIDEO = {
  SONG_VIDEO_FAILED:
    'Song videos stopped early. Everything already saved was kept, and the next sync of this artist will pick up the rest.',
  SONG_VIDEO_DEFERRED:
    'This artist has more songs needing videos than one sync adds. The next sync adds the rest.',
  SONG_VIDEO_DID_NOT_FINISH:
    'Song videos never finished. The releases and songs are all saved, but some videos are missing.',
  CANCELLED: 'This was stopped while song videos were being added. Everything already saved was kept.',
}

// Exported (also used by RequestDetail.jsx's "Problems" section, which
// gathers every per-item warning this page can show into one place - same
// wording, never a second translation of the same code that could drift).
export function videoReason(warning) {
  return VIDEO_REASONS[warning.code] || 'This video could not be added this time.'
}

export function requestVideoReason(warning) {
  const base = REQUEST_VIDEO[warning.code] || 'Song videos did not finish.'
  const cause = warning.cause && VIDEO_CAUSES[warning.cause]
  return cause ? `${base} ${cause}` : base
}

export function artReason(warning) {
  if (warning.code === 'ARTIST_AVATAR_NOT_FOUND') {
    return AVATAR_CAUSES[warning.cause] || ART_REASONS.ARTIST_AVATAR_NOT_FOUND
  }
  return ART_REASONS[warning.code] || 'YouTube’s image could not be used this time.'
}

export function requestArtReason(warning) {
  return REQUEST_ART[warning.code] || 'Cover art did not finish.'
}

/** A catalogue item's own display name by f_id - the same lookup this
 * component's own `nameOf` closure already did, pulled out so RequestDetail's
 * "Problems" section can build "<item name> — <message>" rows identically. */
export function itemNameOf(items, fId) {
  const item = (items || []).find(i => i.f_id === fId)
  const staged = item && item.staged_data
  return (staged && (staged.title || staged.name)) || null
}

/** A lyrics warning's own message - everything except the two request-level
 * codes LyricsSummary already pulls out and shows prominently (LYRICS_
 * DISABLED, LYRICS_BAD_SONG_DATA have no useful per-code sentence here;
 * their own notices are the message). Matches Ingest.jsx's LyricsResultSummary:
 * no plain-English translation exists yet for the rest, so the raw code is
 * shown rather than invented wording. */
export function lyricsWarningReason(warning) {
  return warning.code || 'This could not be checked.'
}

function tally(n, one, many) {
  return `${n} ${n === 1 ? one : many}`
}

/**
 * result.lyrics (content-admin/ingest's own lyrics phase, PRs #598/#602 -
 * see LYRICS_CONTRACT.md §7 `finish`) - same fields and special-cased
 * warnings as Ingest.jsx's own LyricsResultSummary, reworded into 609's
 * style and wrapped in this page's existing summaryBlock/plainList/notice
 * classes rather than main's bare <p>/<details>. Verified against space-4
 * request 52a6bcd8: `{"timed":1,"untimed":0,"warnings":[],"not_found":0,
 * "instrumental":0,"feels_without_timing":[]}`.
 */
function LyricsSummary({ lyrics }) {
  // LYRICS_DISABLED (request-level, owner 2026-10-03): lyrics were off when
  // this ran, so nothing was searched - too important for the collapsed
  // warnings list below, shown plainly instead (Ingest.jsx's own wording).
  const disabled = (lyrics.warnings || []).some(w => w.code === 'LYRICS_DISABLED')
  // LYRICS_BAD_SONG_DATA (request-level, owner 2026-10-06): the lyrics step
  // stopped because a song has bad data - a bug worth seeing, pulled out the
  // same way. `cause` is '<song_f_id>:<code>'.
  const badSongData = (lyrics.warnings || []).find(w => w.code === 'LYRICS_BAD_SONG_DATA')
  const badSongMatch = badSongData?.cause?.match(/^([^:]+):(.+)$/)
  const [, badSongId, badSongCode] = badSongMatch || []
  const otherWarnings = (lyrics.warnings || []).filter(
    w => w.code !== 'LYRICS_DISABLED' && w.code !== 'LYRICS_BAD_SONG_DATA'
  )
  const feelsWithoutTiming = Array.isArray(lyrics.feels_without_timing) ? lyrics.feels_without_timing : []
  return (
    <div className={detail.summaryBlock}>
      <h3 className={detail.summaryHeading}>Lyrics</h3>
      {disabled && <p className={detail.notice}>Lyrics were turned off when this ran — no songs were searched.</p>}
      {badSongData && (
        <p className={detail.notice}>
          Lyrics stopped: song <code className={detail.code}>{badSongId}</code> has bad data ({badSongCode}). This
          is a bug — check the song.
        </p>
      )}
      <p className={detail.sectionNote}>
        Lyrics: {lyrics.timed ?? 0} timed, {lyrics.untimed ?? 0} untimed, {lyrics.not_found ?? 0} not found,{' '}
        {lyrics.instrumental ?? 0} instrumental.
      </p>
      {otherWarnings.length > 0 && (
        <details>
          <summary className={detail.writeSummary}>
            {tally(otherWarnings.length, 'lyrics warning', 'lyrics warnings')}
          </summary>
          <pre className={detail.auditValue}>{JSON.stringify(otherWarnings, null, 2)}</pre>
        </details>
      )}
      {feelsWithoutTiming.length > 0 && (
        <>
          <h4 className={detail.summaryHeading}>Songs with Feels but no timed lyrics (data bug)</h4>
          <p className={detail.sectionNote}>
            These songs have live Feels but no effective lyric timing. Ingest never writes lyrics for them; this
            is a data bug to fix directly.
          </p>
          <ul className={detail.plainList}>
            {feelsWithoutTiming.map(songFID => (
              <li key={songFID}>
                <code className={detail.code}>{songFID}</code>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

LyricsSummary.propTypes = { lyrics: PropTypes.object.isRequired }

export default function CompletionSummary({ result, items = [], writes = [], artistName, status }) {
  if (!result) return null

  // Still 'running' while art and videos are added, with the result saved.
  const stillWorking = status === 'running'
  const art = result.cover_art || null
  const video = result.song_videos || null
  const lyrics = result.lyrics || null
  // The lyrics phase runs AFTER the video phase (API-PAYLOAD.md "Several
  // phases inside two statuses": catalogue -> art -> video -> lyrics), so
  // video's own key being populated while lyrics' is not is the one
  // reliable signal that lyrics is the phase currently running, not just
  // "not reached yet" or "this environment never runs it at all". The one
  // case this cannot tell apart from "running" is a request whose video
  // phase itself never ran (disabled or genuinely nothing to upload) - that
  // shows nothing rather than guessing, per "no claim either way".
  const lyricsRunning = stillWorking && !lyrics && video != null

  const releases = result.releases || 0
  const songs = result.songs || 0
  const seen = result.source_tracks || 0
  const unavailable = Array.isArray(result.unavailable_tracks) ? result.unavailable_tracks : []
  const preserved = Array.isArray(result.preserved_fields) ? result.preserved_fields : []
  // The two commit shapes dedupe differently, so the sentence names no cause.
  const repeats = seen && songs && seen > songs ? seen - songs : 0
  const artWarnings = art && Array.isArray(art.warnings) ? art.warnings : []
  // A request-level f_id is the request uuid, never a catalogue id.
  const requestArt = artWarnings.filter(w => w.kind === 'request')
  const itemArt = artWarnings.filter(w => w.kind !== 'request')
  const videoWarnings = video && Array.isArray(video.warnings) ? video.warnings : []
  const requestVideo = videoWarnings.filter(w => w.kind === 'request')
  const itemVideo = videoWarnings.filter(w => w.kind !== 'request')

  function nameOf(fId) {
    return itemNameOf(items, fId)
  }

  function keptValue(type, fId, field) {
    const write = writes.find(w => w.table_name === TABLE[type] && w.after_data && w.after_data.f_id === fId)
    const value = write && write.after_data ? write.after_data[field] : undefined
    return value === null || value === undefined || value === '' ? null : String(value)
  }

  return (
    <section className={detail.section}>
      <h2 className={detail.sectionTitle}>
        {stillWorking ? 'What this sync has saved so far' : 'What this sync did'}
      </h2>
      <p className={detail.summaryLead}>
        Synced {tally(releases, 'release', 'releases')} and {tally(songs, 'song', 'songs')}
        {artistName ? <> onto <strong>{artistName}</strong></> : null}.
      </p>
      {stillWorking ? (
        <p className={detail.sectionNote}>
          The releases and songs are saved. It is still adding cover art and song videos, which does not
          change them.
        </p>
      ) : null}

      <dl className={detail.facts}>
        <div><dt>Releases</dt><dd>{releases}</dd></div>
        <div><dt>Songs</dt><dd>{songs}</dd></div>
        <div><dt>YouTube tracks seen</dt><dd>{seen}</dd></div>
      </dl>

      {repeats > 0 && (
        <p className={detail.sectionNote}>
          {seen} YouTube tracks became {tally(songs, 'song', 'songs')} — {tally(repeats, 'of them was', 'of them were')}{' '}
          the same recording as another, so they share a song rather than creating near-identical entries.
        </p>
      )}

      {unavailable.length > 0 && (
        <div className={detail.summaryBlock}>
          <h3 className={detail.summaryHeading}>
            {tally(unavailable.length, 'track could not be added', 'tracks could not be added')}
          </h3>
          <p className={detail.sectionNote}>
            These are on the release on YouTube but have no playable video there, so there is no song for them in
            the catalogue. Their place in the track list was left exactly as it was — nothing was removed.
          </p>
          <ul className={detail.plainList}>
            {unavailable.map((t, i) => (
              <li key={`${t.release}-${t.track}-${i}`}>
                {t.name || 'Untitled'}
                {t.release ? <span className={detail.summaryWhere}> — {t.release}, track {t.track}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {art && (
        <div className={detail.summaryBlock}>
          <h3 className={detail.summaryHeading}>Cover art</h3>
          {art.albums || art.artist_images ? (
            <p className={detail.sectionNote}>
              Added cover art to {tally(art.albums || 0, 'release', 'releases')}
              {art.artist_images ? ' and the artist photo' : ''}.
            </p>
          ) : (
            <p className={detail.sectionNote}>No new cover art was added.</p>
          )}

          {requestArt.length > 0 && (
            <ul className={detail.plainList}>
              {requestArt.map(w => (
                <li key={w.code}>{REQUEST_ART[w.code] || 'Cover art did not finish.'}</li>
              ))}
            </ul>
          )}

          {itemArt.length > 0 && (
            <>
              <p className={detail.sectionNote}>
                {tally(itemArt.length, 'image was not added', 'images were not added')}. The releases and songs are
                saved and correct — only the artwork is missing. You can add these by hand.
              </p>
              <ul className={detail.plainList}>
                {itemArt.map((w, i) => (
                  <li key={`${w.f_id}-${w.code}-${i}`}>
                    {nameOf(w.f_id) ? <strong>{nameOf(w.f_id)}</strong> : <code className={detail.code}>{w.f_id}</code>}
                    <span className={detail.summaryWhere}> — {artReason(w)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {video && (
        <div className={detail.summaryBlock}>
          <h3 className={detail.summaryHeading}>Song videos</h3>
          {video.uploaded ? (
            <p className={detail.sectionNote}>
              Added a video to {tally(video.uploaded, 'song', 'songs')}.
            </p>
          ) : (
            <p className={detail.sectionNote}>No new song videos were added.</p>
          )}

          {video.pending > 0 && (
            <p className={detail.sectionNote}>
              {tally(video.pending, 'more was', 'more were')} uploaded but had not finished processing when we
              stopped checking. They may be there now.
            </p>
          )}

          {requestVideo.length > 0 && (
            <ul className={detail.plainList}>
              {requestVideo.map(w => (
                <li key={w.code}>{requestVideoReason(w)}</li>
              ))}
            </ul>
          )}

          {itemVideo.length > 0 && (
            <>
              <p className={detail.sectionNote}>
                {tally(itemVideo.length, 'song did not get a video', 'songs did not get a video')}.
              </p>
              <ul className={detail.plainList}>
                {itemVideo.map((w, i) => (
                  <li key={`${w.f_id}-${w.code}-${i}`}>
                    {nameOf(w.f_id) ? <strong>{nameOf(w.f_id)}</strong> : <code className={detail.code}>{w.f_id}</code>}
                    <span className={detail.summaryWhere}> — {videoReason(w)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {lyrics && <LyricsSummary lyrics={lyrics} />}
      {!lyrics && lyricsRunning && (
        <div className={detail.summaryBlock}>
          <h3 className={detail.summaryHeading}>Lyrics</h3>
          <p className={detail.sectionNote}>Finding lyrics…</p>
        </div>
      )}

      {preserved.length > 0 && (
        <div className={detail.summaryBlock}>
          <h3 className={detail.summaryHeading}>
            {preserved.length === 1 ? 'One value was left alone' : `${preserved.length} entries had values left alone`}
          </h3>
          <p className={detail.sectionNote}>
            YouTube had something different for these, but someone had already set them by hand, so this sync did
            not touch them. If YouTube is the one that is right now, change them yourself.
          </p>
          <ul className={detail.plainList}>
            {preserved.map(entry => {
              const label = nameOf(entry.f_id)
              const fields = Array.isArray(entry.fields) ? entry.fields : []
              return (
                <li key={`${entry.type}-${entry.f_id}`}>
                  {TYPE_LABEL[entry.type] || entry.type}{' '}
                  {label ? <strong>{label}</strong> : <code className={detail.code}>{entry.f_id}</code>}
                  <ul className={detail.plainList}>
                    {fields.map(field => {
                      const kept = keptValue(entry.type, entry.f_id, field)
                      return (
                        <li key={field}>
                          {FIELD_LABEL[field] || field}
                          {kept ? <> — kept as “{kept}”</> : null}
                        </li>
                      )
                    })}
                  </ul>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </section>
  )
}

CompletionSummary.propTypes = {
  result: PropTypes.object,
  items: PropTypes.array,
  writes: PropTypes.array,
  artistName: PropTypes.string,
  status: PropTypes.string,
}
